import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { getUsernameFromAuthData, splitCourseSectionTopicId } from "../utils";
import config from "../config";

const initialSubscription = {
  plan: "basic",
  isUpgradePossible: false,
  isPaidUser: false,
  stripeCustomerId: null,
  subscriptionId: null,
  subscriptionData: {
    plan: {},
  },
  isSubscriptionLoaded: false,
  paymentMethods: {
    object: "list",
    data: [],
    has_more: false,
    url: "/v1/payment_methods",
  },
};

export const userAtom = atom({});

// Backup flag for isCommunityAccessRestricted, learned from classroom data
// when the user's own record isn't flagged yet. Persisted; once true, stays
// true. getOnInit avoids a flash of unrestricted UI on load.
export const communityAccessRestrictedAtom = atomWithStorage(
  "nualang-community-access-restricted",
  false,
  undefined,
  { getOnInit: true },
);

export const markCommunityAccessRestrictedAtom = atom(
  null,
  (get, set, classrooms = []) => {
    if (get(communityAccessRestrictedAtom)) return;
    const isRestricted =
      Array.isArray(classrooms) &&
      classrooms.some((c) => c && c.isCommunityAccessRestricted === true);
    if (isRestricted) {
      set(communityAccessRestrictedAtom, true);
    }
  },
);

// Whether the user's customer blocks community content, from their own user
// record (users-api get_user_details). Unlike the flag above it follows the
// latest load, so allowing it again lifts it. Persisted so a reload doesn't
// flash unrestricted UI before the user loads.
export const communityContentBlockedAtom = atomWithStorage(
  "nualang-community-content-blocked",
  false,
  undefined,
  { getOnInit: true },
);

export const setUserAtom = atom(null, (get, set, authData) => {
  if (!authData) return;
  if (
    authData.signInUserSession &&
    authData.signInUserSession.idToken &&
    authData.signInUserSession.idToken.payload
  ) {
    authData.attributes = {
      ...authData.signInUserSession.idToken.payload,
    };
  }
  const username = getUsernameFromAuthData(authData);
  authData.attributes = { ...(authData.attributes || {}), username };
  set(userAtom, {
    ...authData,
    username,
  });
});

export const loadingAtom = atom(false);

export const startLoadingAtom = atom(null, (get, set) => {
  set(loadingAtom, true);
});

export const stopLoadingAtom = atom(null, (get, set) => {
  set(loadingAtom, false);
});

export const isSnackbarOpenAtom = atom(false);
export const messageAtom = atom("");
export const typeAtom = atom("info");

export const openSnackbarAtom = atom(null, (get, set, message, type) => {
  set(isSnackbarOpenAtom, true);
  set(messageAtom, message);
  set(typeAtom, type);
});

export const closeSnackbarAtom = atom(null, (get, set) => {
  set(isSnackbarOpenAtom, false);
});

export const mobileAtom = atom(
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  ),
);

export const shareScreenOpenAtom = atom(false);

export const closeShareAtom = atom(null, (get, set) => {
  set(shareScreenOpenAtom, false);
});

export const shareUrlAtom = atom("");
export const creatorSubscriptionAtom = atom(initialSubscription);
export const isModerateDialogOpenAtom = atom(false);
export const moderationReasonAtom = atom("");

export const openModerateDialogAtom = atom(
  null,
  (get, set, moderationLabel) => {
    set(moderationReasonAtom, moderationLabel);
    set(isModerateDialogOpenAtom, true);
  },
);

export const closeModerateDialogAtom = atom(null, (get, set) => {
  set(isModerateDialogOpenAtom, false);
});

export const isCollapsedAtom = atom(false);

export const shareRoleplayAtom = atom(null, async (get, set, roleplay) => {
  const { courseSectionTopicId, roleplayId } = roleplay;
  let url;
  if (courseSectionTopicId && courseSectionTopicId.includes("|")) {
    const { courseId, sectionId, topicId } =
      splitCourseSectionTopicId(courseSectionTopicId);
    url = `${config.APP_URL}/courses/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}`;
  } else {
    url = `${config.APP_URL}/roleplays/${roleplayId}`;
  }
  set(shareUrlAtom, url);
  if (navigator.share) {
    try {
      await navigator.share({
        title: roleplay.roleplayName,
        text: roleplay.roleplayName,
        url,
      });
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);
      }
    }
  } else {
    set(shareScreenOpenAtom, true);
  }
});

export const shareBotAtom = atom(null, async (get, set, bot) => {
  const { courseSectionTopicId, botId } = bot;
  let url;
  if (courseSectionTopicId && courseSectionTopicId.includes("|")) {
    const { courseId, sectionId, topicId } =
      splitCourseSectionTopicId(courseSectionTopicId);
    url = `${config.APP_URL}/courses/${courseId}/${sectionId}/${topicId}/bots/${botId}`;
  } else {
    url = `${config.APP_URL}/bots/${botId}`;
  }
  set(shareUrlAtom, url);
  if (navigator.share) {
    try {
      await navigator.share({ title: bot.botName, text: bot.botName, url });
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);
      }
    }
  } else {
    set(shareScreenOpenAtom, true);
  }
});
